import React, { useState } from 'react';
import { Check, ArrowUpRight } from 'lucide-react';
import AuditModal from './AuditModal';

const tiers = [
  {
    id: 'consultation',
    name: 'Consultation',
    price: '$100',
    cadence: '/ hour',
    unit: 'One-time',
    blurb:
      "I go through your email setup live, tell you what's broken, what's working, and exactly what I'd do next. You leave with a written action list whether we work together or not.",
    features: [],
    cta: 'Book a call',
    highlight: false,
  },
  {
    id: 'starter',
    name: 'Starter',
    price: '$1,500',
    cadence: '/ month',
    unit: 'Monthly',
    blurb: 'For solopreneurs getting email running properly.',
    features: [
      '2 broadcasts per week (newsletter and promos)',
      'Nurture flow refresh',
      'Up to 3 automations built and maintained',
      'Deliverability monitoring',
      'Light segmentation and list hygiene',
      'Monthly strategy call and Slack support',
    ],
    cta: 'Get started',
    highlight: false,
  },
  {
    id: 'growth',
    name: 'Growth',
    price: '$2,500',
    cadence: '/ month',
    unit: 'Monthly',
    blurb: 'For creators ready to monetize their list.',
    features: [
      '4 broadcasts per week and 1 full campaign per month',
      'Full automation rebuild',
      'A/B testing on subject lines and sends',
      'Cold email structure and monetization strategy',
      'ManyChat and chatbot integration',
      'Bi-weekly strategy call and priority Slack support',
    ],
    cta: 'Get started',
    highlight: true,
  },
  {
    id: 'scale',
    name: 'Scale',
    price: '$5,000',
    cadence: '/ month',
    unit: 'Monthly',
    blurb: 'For businesses that want the whole system owned.',
    features: [
      'Everything in Growth',
      'Multi-platform management (email, ManyChat, CRM)',
      'Funnel and landing page optimization',
      'Platform migration or re-architecture when needed',
      'Weekly strategy call, same-day Slack response',
    ],
    cta: 'Talk to Akash',
    highlight: false,
  },
];

const PricingTiers = () => {
  const [open, setOpen] = useState(false);
  return (
    <section className="container-x section" data-testid="pricing-section">
      <div className="text-center max-w-3xl mx-auto mb-6">
        <p className="text-xs uppercase tracking-[0.25em] text-[var(--gh-orange)] font-semibold">
          Engagements
        </p>
        <h2 className="font-display text-4xl md:text-5xl font-extrabold mt-3 text-ink leading-[1.05]">
          Choose the right plan for{' '}
          <span className="font-serif italic text-[var(--gh-orange)]">your stage</span>
        </h2>
      </div>

      {/* 3-month minimum sub-line, always visible above the cards */}
      <div
        className="max-w-3xl mx-auto mb-12 rounded-2xl bg-cream-2 border border-line px-5 py-4 text-center"
        data-testid="pricing-min-note"
      >
        <p className="text-sm md:text-[15px] text-ink leading-relaxed">
          All retainers have a 3-month minimum. Not sure? Book a consultation first. If we start working together within 30 days, the $100 is credited to your first invoice.
        </p>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5 items-stretch">
        {tiers.map((t) => {
          const highlight = t.highlight;
          return (
            <div
              key={t.id}
              data-testid={`pricing-tier-${t.id}`}
              className={`relative rounded-[1.75rem] p-7 md:p-8 flex flex-col transition-all border ${
                highlight
                  ? 'bg-[var(--gh-orange)] text-[var(--gh-cream)] border-transparent shadow-[0_30px_60px_-30px_rgba(234,88,12,0.6)] xl:scale-[1.04] xl:-my-2'
                  : 'bg-cream border-line hover:-translate-y-1 hover:border-ink'
              }`}
            >
              {highlight && (
                <span
                  data-testid="pricing-most-popular-badge"
                  className="absolute -top-3 left-1/2 -translate-x-1/2 bg-ink text-[var(--gh-cream)] text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full whitespace-nowrap"
                >
                  Most popular
                </span>
              )}

              <div className="flex items-baseline justify-between">
                <h3 className="font-display text-2xl font-bold">{t.name}</h3>
                <span
                  className={`text-[10px] uppercase tracking-widest ${
                    highlight ? 'text-white/70' : 'text-muted-warm'
                  }`}
                >
                  {t.unit}
                </span>
              </div>

              <div className="mt-4 flex items-end gap-1">
                <span className="font-display text-4xl md:text-5xl font-extrabold leading-none">
                  {t.price}
                </span>
                <span
                  className={`pb-1.5 text-sm ${
                    highlight ? 'text-white/75' : 'text-muted-warm'
                  }`}
                >
                  {t.cadence}
                </span>
              </div>

              <p
                className={`mt-4 text-sm leading-relaxed ${
                  highlight ? 'text-white/90' : 'text-muted-warm'
                }`}
              >
                {t.blurb}
              </p>

              <button
                onClick={() => setOpen(true)}
                data-testid={`pricing-tier-${t.id}-cta`}
                className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 font-semibold transition-colors ${
                  highlight
                    ? 'bg-[var(--gh-cream)] text-[var(--gh-ink)] hover:bg-white'
                    : 'bg-ink text-[var(--gh-cream)] hover:bg-black'
                }`}
              >
                {t.cta} <ArrowUpRight className="w-4 h-4" />
              </button>

              {t.features.length > 0 && (
                <ul className="mt-7 space-y-3">
                  {t.features.map((f) => (
                    <li
                      key={f}
                      className={`flex items-start gap-3 text-sm ${
                        highlight ? 'text-white/95' : 'text-ink'
                      }`}
                    >
                      <span
                        className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                          highlight
                            ? 'bg-white/25'
                            : 'bg-[var(--gh-orange-soft)] text-[var(--gh-orange)]'
                        }`}
                      >
                        <Check className="w-3 h-3" />
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-center mt-10 text-sm text-muted-warm">
        Need something one-time (audit, migration, deliverability rescue)?{' '}
        <a
          href="/contact"
          className="text-[var(--gh-orange)] font-semibold hover:underline"
        >
          Tell me about it.
        </a>
      </p>

      <AuditModal isOpen={open} onClose={() => setOpen(false)} />
    </section>
  );
};

export default PricingTiers;
